import type { TFunction } from 'i18next';
import DeleteIcon from '../../assets/icons/delete.svg?react';
import EditIcon from '../../assets/icons/edit.svg?react';
import type { PatientRoomPropertyUsageResponse } from '../../api/generated';
import { EditableCell, EditingActions, tertiaryIconButtonStyle } from '../UI';
import type { TableColumn } from '../UI';
import { ROOM_NEED_NAME_COLUMN_KEY } from './roomNeedsConstants';
import { Button } from '../UI/base/Button';
import columnStyles from '../RoomsSettings/roomColumns.module.css';
import { RoomCountCell } from './RoomCountCell';
import { CommentCell } from './CommentCell';

interface RoomNeedsColumnCallbacks {
  editingPropertyId: string | null;
  editingName: string;
  editingComment: string;
  onStartEdit: (property: PatientRoomPropertyUsageResponse) => void;
  onNameChange: (value: string) => void;
  onCommentChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: (property: PatientRoomPropertyUsageResponse) => void;
}

export const getRoomNeedsColumns = (
  t: TFunction,
  cb: RoomNeedsColumnCallbacks
): TableColumn<PatientRoomPropertyUsageResponse>[] => [
  {
    key: ROOM_NEED_NAME_COLUMN_KEY,
    header: t('settings-col-need-name'),
    width: 237,
    sortable: true,
    // The cell renders an EditableCell around the name, which carries no order.
    sortValue: (property: PatientRoomPropertyUsageResponse) => property.name,
    render: (property) => (
      <EditableCell
        value={property.name}
        isEditing={cb.editingPropertyId === property.id}
        name={cb.editingName}
        onNameChange={cb.onNameChange}
        onSave={cb.onSave}
        onCancel={cb.onCancel}
      />
    )
  },
  {
    key: 'associated-rooms',
    header: t('settings-col-associated-rooms'),
    cellStyle: { flex: '1 1 0', flexBasis: 0, minWidth: 200 },
    render: (property) => <RoomCountCell count={property.patientRoomCount} />
  },
  {
    key: 'comment',
    header: t('settings-col-comment'),
    cellStyle: { flex: '1 1 0', flexBasis: 0, minWidth: 200 },
    render: (property) => (
      <CommentCell
        value={property.description ?? ''}
        isEditing={cb.editingPropertyId === property.id}
        draft={cb.editingComment}
        onDraftChange={cb.onCommentChange}
        onSave={cb.onSave}
        onCancel={cb.onCancel}
      />
    )
  },
  {
    key: 'edit',
    header: t('settings-col-edit'),
    width: 100,
    headerAlign: 'center',
    cellStyle: { alignItems: 'center' },
    render: (property) => {
      const isEditingThisRow = cb.editingPropertyId === property.id;
      if (isEditingThisRow) {
        const canSave = cb.editingName.trim().length > 0;
        return <EditingActions canSave={canSave} onSave={cb.onSave} onCancel={cb.onCancel} />;
      }
      return (
        <Button
          type="tertiary"
          onClick={() => cb.onStartEdit(property)}
          styles={{ root: tertiaryIconButtonStyle }}
        >
          <EditIcon className={columnStyles.actionIcon} />
        </Button>
      );
    }
  },
  {
    key: 'delete',
    header: t('settings-col-delete'),
    width: 100,
    headerAlign: 'center',
    cellStyle: { alignItems: 'center' },
    render: (property) => (
      <Button
        type="tertiary"
        onClick={() => cb.onDelete(property)}
        styles={{ root: tertiaryIconButtonStyle }}
      >
        <DeleteIcon className={columnStyles.actionIcon} />
      </Button>
    )
  }
];
